<template>
    <div class="min-h-screen bg-slate-50 text-slate-800 font-sans selection:bg-orange-500 selection:text-white">
        
        <!-- ── LOADING ──────────────────────────────────────────────── -->
        <div v-if="loading" class="min-h-screen flex flex-col items-center justify-center bg-white">
            <div class="relative w-10 h-10">
                <div class="absolute inset-0 border-4 border-orange-100 rounded-full"></div>
                <div class="absolute inset-0 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p class="text-xs font-medium tracking-widest uppercase text-slate-400 mt-4">
                Chargement du suivi
            </p>
        </div>

        <!-- ── ERREUR / 404 ─────────────────────────────────────────── -->
        <div v-else-if="error" class="min-h-screen flex items-center justify-center px-6 bg-slate-50">
            <div class="text-center max-w-sm bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
                <div class="w-12 h-12 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                    </svg>
                </div>
                <h2 class="text-lg font-semibold text-slate-900 mb-1">Lien introuvable ou expiré</h2>
                <p class="text-sm text-slate-500 leading-relaxed mb-6">
                    Ce lien d'accès au chantier n'est plus valide ou a été désactivé par l'administrateur.
                </p>
                <span class="inline-block text-xs font-mono px-2.5 py-1 bg-slate-100 text-slate-500 rounded-md">Erreur 404</span>
            </div>
        </div>

        <!-- ══════════════════════════════════════════════════════════
             CONTENU PRINCIPAL
        ══════════════════════════════════════════════════════════════ -->
        <div v-else-if="chantier" class="max-w-3xl mx-auto px-4 py-8 sm:px-6 lg:py-12">
            
            <!-- ── EN-TÊTE DE PAGE ────────────────────────────────────── -->
            <header class="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-100 mb-6">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                    <div class="flex items-start gap-4">
                        <!-- Logo ou Initiale Propre -->
                        <!-- <div class="flex-shrink-0">
                            <img v-if="chantier.societe.logo" :src="chantier.societe.logo" class="h-12 w-12 object-contain rounded-xl bg-slate-50 p-2 border border-slate-100" :alt="chantier.societe.nom" />
                            <div v-else class="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-semibold text-lg shadow-sm">
                                {{ initial }}
                            </div>
                        </div> -->
                        
                        <!-- Infos Société & Chantier -->
                        <div>
                            <span class="text-xs font-semibold tracking-wider text-orange-600 uppercase">Espace Client</span>
                            <h1 class="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mt-0.5">
                                {{ chantier.societe.nom }}
                            </h1>
                            <p class="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-slate-400 flex-shrink-0">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                                </svg>
                                {{ chantier.adresse.adresse1 }}, {{ chantier.adresse.code_postal }} {{ chantier.adresse.ville }}
                            </p>
                        </div>
                    </div>

                    <!-- Actions Contacts Rénovées -->
                    <div v-if="chantier.societe.tel || chantier.societe.mail" class="flex flex-wrap gap-2 pt-4 sm:pt-0 border-t border-slate-100 sm:border-0">
                        <a v-if="chantier.societe.tel" :href="`tel:${chantier.societe.tel}`" class="inline-flex items-center justify-center gap-2 px-3.5 py-2 text-xs font-medium bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-xl transition border border-slate-200">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5 text-slate-500">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-2.824-1.802-5.14-4.117-6.944-6.944l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                            </svg>
                            Appeler
                        </a>
                        <a v-if="chantier.societe.mail" :href="`mailto:${chantier.societe.mail}`" class="inline-flex items-center justify-center gap-2 px-3.5 py-2 text-xs font-medium bg-orange-50 hover:bg-orange-100/80 text-orange-700 rounded-xl transition">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5 text-orange-600">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                            </svg>
                            Email
                        </a>
                    </div>
                </div>

                <!-- Progression fine intégrée -->
                <!-- <div v-if="chantier.passages.length" class="mt-6 pt-6 border-t border-slate-100">
                    <div class="flex items-center justify-between text-xs text-slate-500 mb-2">
                        <span class="font-medium">Progression des interventions</span>
                        <span class="font-bold text-slate-900">{{ progressionPct }}%</span>
                    </div>
                    <div class="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div class="h-full bg-orange-600 rounded-full transition-all duration-1000 ease-out" :style="`width:${progressionPct}%`"></div>
                    </div>
                </div> -->
            </header>

            <!-- ── GRID DE STATISTIQUES CONTEMPORAINES ─────────────────── -->
            <!-- <div class="grid grid-cols-3 gap-3 mb-8">
                <div class="bg-white p-4 rounded-xl border border-slate-100 shadow-sm text-center sm:text-left">
                    <p class="text-xs font-medium text-slate-400 uppercase tracking-wider">Total</p>
                    <p class="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">{{ chantier.passages.length }}</p>
                </div>
                <div class="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100 shadow-sm text-center sm:text-left">
                    <p class="text-xs font-medium text-emerald-600 uppercase tracking-wider">Réalisés</p>
                    <p class="text-2xl sm:text-3xl font-bold text-emerald-700 mt-1">{{ passagesTermines }}</p>
                </div>
                <div class="bg-orange-50/50 p-4 rounded-xl border border-orange-100 shadow-sm text-center sm:text-left">
                    <p class="text-xs font-medium text-orange-600 uppercase tracking-wider">À venir</p>
                    <p class="text-2xl sm:text-3xl font-bold text-orange-700 mt-1">{{ passagesRestants }}</p>
                </div>
            </div> -->

            <!-- ── PASSAGES PLANIFIÉS ─────────────────────────────────── -->
            <section class="mb-8">
                <h3 class="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <span class="w-1.5 h-3.5 bg-orange-600 rounded-full"></span>
                    Passages planifiés
                </h3>

                <!-- Aucun passage -->
                <div v-if="!chantier.passages.length" class="bg-white rounded-2xl p-8 text-center border border-slate-100 shadow-sm">
                    <p class="text-sm text-slate-400">Aucun passage planifié pour le moment.</p>
                </div>

                <!-- Liste passages modernisée -->
                <div v-else class="space-y-3">
                    <div v-for="passage in chantier.passages" :key="passage.id" class="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-slate-200 transition">
                        
                        <!-- Bloc Date & Infos principal -->
                        <div class="flex items-center gap-4">
                            <!-- Vignette type calendrier épuré -->
                            <div class="w-14 h-14 bg-slate-50 border border-slate-100 rounded-xl flex flex-col items-center justify-center flex-shrink-0">
                                <span class="text-xl font-bold text-slate-900 leading-none">{{ dayNumber(passage.date) }}</span>
                                <span class="text-[10px] font-medium text-slate-400 uppercase tracking-wider mt-1">{{ monthShort(passage.date) }}</span>
                            </div>

                            <div>
                                <h4 class="font-semibold text-slate-900 text-sm capitalize">
                                    {{ monthWeekday(passage.date) }}
                                </h4>
                                <div class="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-xs text-slate-500">
                                    <span class="inline-flex items-center gap-1 font-medium text-slate-700">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5 text-slate-400">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                                        </svg>
                                        {{ passage.moment_day === 'morning' ? 'Matin' : 'Après-midi' }}
                                    </span>
                                    <span v-if="passage.intervention && passage.intervention.type" class="text-slate-300">•</span>
                                    <span v-if="passage.intervention && passage.intervention.type" class="text-slate-400">
                                        {{ passage.intervention.type }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Statut & Technicien (Droite) -->
                        <div class="flex sm:flex-col items-center sm:items-end justify-between border-t border-slate-50 pt-3 sm:pt-0 sm:border-0 gap-3">
                            <!-- Badge Statut -->
                            <span v-if="passage.statut" class="text-[11px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wide" :style="`color:${passage.statut.couleur}; background:${passage.statut.couleur}09; border: 1px solid ${passage.statut.couleur}15;`">
                                {{ passage.statut.libelle }}
                            </span>

                            <!-- Technicien -->
                            <div v-if="passage.intervention && passage.intervention.technicien" class="flex items-center gap-2">
                                <div class="w-5 h-5 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                                    <span class="text-[9px] font-bold text-slate-600">
                                        {{ passage.intervention.technicien[0].toUpperCase() }}
                                    </span>
                                </div>
                                <span class="text-xs font-medium text-slate-500">
                                    {{ passage.intervention.technicien }}
                                </span>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <!-- ── INTERVENTIONS PRÉVUES ──────────────────────────────── -->
            <div v-if="chantier.interventions && chantier.interventions.length">
                <h3 class="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <span class="w-1.5 h-3.5 bg-orange-600 rounded-full"></span>
                    Prestations prévues
                </h3>
                <section class="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">

                    <div class="divide-y divide-slate-100">
                        <div v-for="intervention in chantier.interventions" :key="intervention.id" class="py-4 first:pt-0 last:pb-0 flex items-start gap-4">
                            <div class="flex-1 min-w-0">
                                <div class="flex items-center justify-between gap-4 flex-wrap">
                                    <h4 class="font-semibold text-slate-900 text-sm sm:text-base">
                                        {{ intervention.type || 'Intervention' }}
                                    </h4>
                                    <span v-if="intervention.statut" class="inline-flex items-center text-[11px] font-medium px-2.5 py-0.5 rounded-full" :style="`color:${intervention.statut.couleur}; background:${intervention.statut.couleur}10;`">
                                        {{ intervention.statut.libelle }}
                                    </span>
                                </div>

                                <p v-if="intervention.description" class="text-sm text-slate-500 mt-1 leading-relaxed">
                                    {{ intervention.description }}
                                </p>

                                <div v-if="intervention.appareil" class="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-400 bg-slate-50 px-2.5 py-1 rounded-md border border-slate-100">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5 text-slate-400">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
                                    </svg>
                                    <span>
                                        <span v-if="intervention.appareil.marque" class="font-medium text-slate-600">
                                            {{ intervention.appareil.marque }} <template v-if="intervention.appareil.model">({{ intervention.appareil.model }})</template>
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            chantier: null,
            loading:  true,
            error:    false,
        }
    },

    computed: {
        initial() {
            return (this.chantier?.societe?.nom || '?')[0].toUpperCase()
        },
        passagesTermines() {
            return this.chantier?.passages.filter(p => p.statut?.code === 'termine').length ?? 0
        },
        passagesRestants() {
            return this.chantier?.passages.filter(p => p.statut?.code !== 'termine').length ?? 0
        },
        progressionPct() {
            const total = this.chantier?.passages.length ?? 0
            if (!total) return 0
            return Math.round((this.passagesTermines / total) * 100)
        },
    },

    methods: {
        async getChantier() {
            try {
                const response = await this.$request.post('chantier-client/get-public-chantier', {
                    chantier_token: this.$route.params.token,
                })
                this.chantier = response
            } catch {
                this.error = true
            } finally {
                this.loading = false
            }
        },

        dayNumber(dateStr) {
            return new Date(dateStr + 'T12:00:00').getDate()
        },

        monthShort(dateStr) {
            return new Date(dateStr + 'T12:00:00').toLocaleDateString('fr-FR', {
                month: 'short',
            }).replace('.', '')
        },

        monthWeekday(dateStr) {
            return new Date(dateStr + 'T12:00:00').toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
            })
        },
    },

    mounted() {
        // Uniquement DM sans (Polices épurées)
        const link = document.createElement('link')
        link.rel  = 'stylesheet'
        link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap'
        document.head.appendChild(link)

        this.getChantier()
    },
}
</script>